<template>
  <div class="modal fade" tabindex="-1" id="TissBatchDetailModal"
       data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Detalhes do Lote: {{ batch?.batch_number }}</h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2"
               @click="closeModal()"
               data-bs-dismiss="modal" aria-label="Fechar">
            <i class="bi bi-x-lg"></i>
          </div>
        </div>

        <div class="modal-body" v-if="batch">
          <!-- Informações do Lote -->
          <div class="card mb-5">
            <div class="card-header">
              <h6 class="fw-bold mb-0">Informações do Lote</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-3">
                  <label class="text-muted">Número do Lote</label>
                  <div class="fw-bold">{{ batch.batch_number }}</div>
                </div>
                <div class="col-md-3">
                  <label class="text-muted">Tipo</label>
                  <div>
                    <span :class="batchTypeClass(batch.batch_type)">
                      {{ batchTypeLabel(batch.batch_type) }}
                    </span>
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="text-muted">Status</label>
                  <div>
                    <span :class="statusClass(batch.status)">
                      {{ statusLabel(batch.status) }}
                    </span>
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="text-muted">Data de Criação</label>
                  <div>{{ formatDate(batch.created_at) }}</div>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-md-3">
                  <label class="text-muted">Total de Itens</label>
                  <div class="fw-bold">{{ displayItems.length }}</div>
                  <small class="text-muted">
                    {{ displayGuidesCount }} guias{{ batch?.batch_type === 'payment' ? ', ' + (manualItemsCount || 0) + ' avulsos' : '' }}
                  </small>
                </div>
                <div class="col-md-3">
                  <label class="text-muted">Valor Total</label>
                  <div class="fw-bold text-primary">R$ {{ formatCurrency(batch.total_amount) }}</div>
                </div>
                <div class="col-md-3" v-if="batch.closed_at">
                  <label class="text-muted">Data de Fechamento</label>
                  <div>{{ formatDate(batch.closed_at) }}</div>
                </div>
                <div class="col-md-3" v-if="batch.sent_at">
                  <label class="text-muted">Data de Envio</label>
                  <div>{{ formatDate(batch.sent_at) }}</div>
                </div>
              </div>
              <div class="row mt-3" v-if="batch.notes">
                <div class="col-md-12">
                  <label class="text-muted">Observações</label>
                  <div>{{ batch.notes }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Ações do Lote -->
          <div class="card mb-5">
            <div class="card-header">
              <h6 class="fw-bold mb-0">Ações</h6>
            </div>
            <div class="card-body">
              <div class="d-flex flex-wrap gap-2">
                <button v-if="batch.status === 'draft'" 
                        class="btn btn-primary"
                        @click="closeBatch"
                        :disabled="loading">
                  <i class="bi bi-lock"></i> Fechar Lote
                </button>
                
                <button v-if="batch.batch_type === 'tiss' && batch.status === 'closed'" 
                        class="btn btn-success"
                        @click="generateTiss"
                        :disabled="loading">
                  <i class="bi bi-file-earmark-code"></i> Gerar XML
                </button>
                
                <button v-if="batch.batch_type === 'tiss' && ['closed', 'sent'].includes(batch.status) && !hasBillsToReceive"
                        class="btn btn-info"
                        @click="generateBillsToReceive"
                        :disabled="loading">
                  <i class="bi bi-arrow-down-circle"></i> Gerar Contas a Receber
                </button>
                
                <button v-if="batch.batch_type === 'payment' && batch.status === 'closed' && !hasBillsToPay"
                        class="btn btn-warning"
                        @click="generateBillsToPay"
                        :disabled="loading">
                  <i class="bi bi-arrow-up-circle"></i> Gerar Contas a Pagar
                </button>

                <div v-if="batch.batch_type === 'payment' && batch.status === 'closed'" class="dropdown">
                  <button class="btn btn-info dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          :disabled="exportLoading || !hasTaxSettlements">
                    <span v-if="exportLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i v-else class="bi bi-file-earmark-pdf"></i>
                    Apuração Fiscal
                  </button>
                  <ul class="dropdown-menu">
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="exportPdf('summary')">
                        <i class="bi bi-file-text me-2"></i>
                        <div class="d-inline-block">
                          <div>Relatório Resumido</div>
                          <small class="text-muted">Totais consolidados por tipo de prestador</small>
                        </div>
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="exportPdf('detailed')">
                        <i class="bi bi-file-text-fill me-2"></i>
                        <div class="d-inline-block">
                          <div>Relatório Detalhado</div>
                          <small class="text-muted">Lista completa de todos os prestadores</small>
                        </div>
                      </a>
                    </li>
                  </ul>
                </div>
                <small v-if="batch.batch_type === 'payment' && batch.status !== 'draft' && !hasTaxSettlements"
                       class="text-muted ms-2">
                  (Nenhuma apuração fiscal encontrada)
                </small>

                <div v-if="batch.batch_type === 'tiss' && ['closed', 'sent'].includes(batch.status)" class="dropdown">
                  <button class="btn btn-info dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          :disabled="exportLoading || displayGuidesCount === 0">
                    <span v-if="exportLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i v-else class="bi bi-file-earmark-pdf"></i>
                    Relatórios
                  </button>
                  <ul class="dropdown-menu">
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="exportReceiptPdf('summary')">
                        <i class="bi bi-file-text me-2"></i>
                        <div class="d-inline-block">
                          <div>Relatório Resumido</div>
                          <small class="text-muted">Totais consolidados por convênio</small>
                        </div>
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="exportReceiptPdf('detailed')">
                        <i class="bi bi-file-text-fill me-2"></i>
                        <div class="d-inline-block">
                          <div>Relatório Detalhado</div>
                          <small class="text-muted">Lista completa de convênios/clientes</small>
                        </div>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Contas a Receber -->
          <div class="card mb-5" v-if="batch.batch_type === 'tiss' && hasBillsToReceive">
            <div class="card-header">
              <h6 class="fw-bold mb-0">
                Contas a Receber
                <span class="badge badge-info ms-2">{{ displayBillsToReceive.length }}</span>
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Descrição</th>
                      <th>Cliente</th>
                      <th>Valor</th>
                      <th>Vencimento</th>
                      <th>Status</th>
                      <th class="text-end">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="bill in displayBillsToReceive" :key="bill.id">
                      <td>{{ bill.description }}</td>
                      <td>{{ bill.customer?.name || '-' }}</td>
                      <td>R$ {{ formatCurrency(bill.amount - (bill.discount || 0)) }}</td>
                      <td>{{ formatDate(bill.due_date) }}</td>
                      <td>
                        <span :class="billReceiveStatusClass(bill.status)">
                          {{ billReceiveStatusLabel(bill.status) }}
                        </span>
                      </td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="btn btn-sm btn-light-primary"
                          :disabled="customerReportLoadingBillId === billReceiveId(bill)"
                          @click="exportCustomerReport(bill)"
                          title="Relatório detalhado do cliente"
                        >
                          <span v-if="customerReportLoadingBillId === billReceiveId(bill)" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="bi bi-file-earmark-pdf"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Contas a Pagar -->
          <div class="card mb-5" v-if="batch.batch_type === 'payment' && hasBillsToPay">
            <div class="card-header">
              <h6 class="fw-bold mb-0">
                Contas a Pagar
                <span class="badge badge-warning ms-2">{{ displayBillsToPay.length }}</span>
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Descrição</th>
                      <th>Fornecedor</th>
                      <th>Valor</th>
                      <th>Vencimento</th>
                      <th>Status</th>
                      <th class="text-end">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="bill in displayBillsToPay" :key="bill.id">
                      <td>{{ bill.description }}</td>
                      <td>{{ bill.supplier?.name || '-' }}</td>
                      <td>R$ {{ formatCurrency(bill.amount - (bill.discount || 0)) }}</td>
                      <td>{{ formatDate(bill.due_date) }}</td>
                      <td>
                        <span :class="billPayStatusClass(bill.status)">
                          {{ billPayStatusLabel(bill.status) }}
                        </span>
                      </td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="btn btn-sm btn-light-primary"
                          :disabled="supplierReportLoadingBillId === billId(bill)"
                          @click="exportSupplierReport(bill)"
                          title="Relatório detalhado do fornecedor"
                        >
                          <span v-if="supplierReportLoadingBillId === billId(bill)" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="bi bi-file-earmark-pdf"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Lista de Itens -->
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="fw-bold mb-0">
                Itens do Lote
                <span class="badge badge-primary ms-2">{{ displayItems.length }}</span>
                <small class="text-muted ms-2">
                  ({{ displayGuidesCount }} guias{{ batch?.batch_type === 'payment' ? ', ' + (manualItemsCount || 0) + ' avulsos' : '' }})
                </small>
              </h6>

              <!-- Botões para lote em rascunho -->
              <div v-if="batch?.status === 'draft'" class="d-flex gap-2">
                <button type="button"
                        class="btn btn-sm btn-primary"
                        @click.prevent="openAddGuidesModal"
                        title="Adicionar guias ao lote">
                  <i class="bi bi-plus-lg"></i> Adicionar guias
                </button>
                <button v-if="batch?.batch_type === 'payment'"
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        @click.prevent="openAddItemModal"
                        title="Adicionar Item Avulso">
                  <i class="bi bi-plus-lg"></i> Adicionar item avulso
                </button>
              </div>
            </div>
            <div class="card-body">
              <div class="table-responsive" v-if="displayItems.length > 0">
                <table class="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Paciente</th>
                      <th>Profissional</th>
                      <th>Valor</th>
                      <th v-if="batch?.status === 'draft'">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in displayItems" :key="item.type === 'guide' ? item.guide?.id_tiss_guide ?? item.guide?.id : item.id">
                      <td>
                        <span v-if="item.type === 'guide'" class="badge badge-primary">Guia</span>
                        <span v-else class="badge badge-info">Avulso</span>
                      </td>
                      <td>
                        <div v-if="item.type === 'guide'">
                          {{ item.guide.guide_number }}
                        </div>
                        <div v-else>
                          {{ item.description }}
                          <small v-if="item.item?.notes" class="text-muted d-block">{{ item.item.notes }}</small>
                        </div>
                      </td>
                      <td>
                        <span v-if="item.patient">{{ item.patient.patient_name }}</span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span v-if="item.professional">{{ item.professional.prof_name }}</span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>R$ {{ formatCurrency(item.amount) }}</td>
                      <td v-if="batch?.status === 'draft'">
                        <button v-if="item.type === 'guide'"
                                class="btn btn-sm btn-outline-danger me-1"
                                @click="removeGuide(item)"
                                title="Remover guia do lote">
                          <i class="bi bi-trash"></i>
                        </button>
                        <button v-if="item.type === 'manual'"
                                class="btn btn-sm btn-outline-danger"
                                @click="removeItem(item)"
                                title="Remover Item">
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center text-muted py-5">
                Nenhum item encontrado
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal()">Fechar</button>
        </div>
      </div>
    </div>

    <!-- Modal Adicionar guias ao lote -->
    <div class="modal fade" tabindex="-1" id="AddGuidesToBatchModal"
         data-bs-keyboard="false" data-bs-backdrop="static">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Adicionar guias ao lote</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2"
                 @click="closeAddGuidesModal"
                 data-bs-dismiss="modal" aria-label="Fechar">
              <i class="bi bi-x-lg"></i>
            </div>
          </div>
          <div class="modal-body">
            <p class="text-muted small">Selecione as guias disponíveis para este tipo de lote ({{ batchTypeLabel(batch?.batch_type) }}).</p>
            <div v-if="loadingAvailableGuides" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span> Carregando guias disponíveis...
            </div>
            <div v-else-if="availableGuidesForBatch.length === 0" class="text-center text-muted py-4">
              Nenhuma guia disponível para adicionar a este lote.
            </div>
            <div v-else class="table-responsive" style="max-height: 320px; overflow-y: auto;">
              <table class="table table-sm table-bordered">
                <thead class="table-light sticky-top">
                  <tr>
                    <th style="width: 40px;">
                      <input type="checkbox"
                             :checked="selectedGuideIdsForAdd.length === availableGuidesForBatch.length"
                             :indeterminate.prop="selectedGuideIdsForAdd.length > 0 && selectedGuideIdsForAdd.length < availableGuidesForBatch.length"
                             @change="toggleSelectAllAvailableGuides"
                             class="form-check-input">
                    </th>
                    <th>Número</th>
                    <th>Paciente</th>
                    <th>Profissional</th>
                    <th>Operadora</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="g in availableGuidesForBatch" :key="guideIdForAdd(g)">
                    <td>
                      <input type="checkbox"
                             :value="guideIdForAdd(g)"
                             v-model="selectedGuideIdsForAdd"
                             class="form-check-input">
                    </td>
                    <td>{{ g.guide_number }}</td>
                    <td>{{ g.patient_name || '-' }}</td>
                    <td>{{ g.professional_name || '-' }}</td>
                    <td>{{ g.insurance_name || '-' }}</td>
                    <td>R$ {{ formatCurrency(guideAmount(g)) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddGuidesModal">Cancelar</button>
            <button type="button"
                    class="btn btn-primary"
                    :disabled="addingGuides || selectedGuideIdsForAdd.length === 0"
                    @click="addGuidesToBatch">
              <span v-if="addingGuides" class="spinner-border spinner-border-sm me-2"></span>
              Adicionar ({{ selectedGuideIdsForAdd.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Adicionar Item Avulso -->
    <div class="modal fade" tabindex="-1" id="AddItemModal"
         data-bs-keyboard="false" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Adicionar Item Avulso</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2"
                 @click="closeAddItemModal"
                 data-bs-dismiss="modal" aria-label="Fechar">
              <i class="bi bi-x-lg"></i>
            </div>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-12 mb-3">
                <label class="form-label required">Descrição *</label>
                <input
                  v-model="newItem.description"
                  type="text"
                  class="form-control"
                  placeholder="Descrição do item avulso"
                  maxlength="255">
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label required">Valor *</label>
                <input
                  v-model="newItem.amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="form-control"
                  placeholder="0,00">
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Profissional</label>
                <select v-model="newItem.id_prof" class="form-select">
                  <option value="">Selecione um profissional</option>
                  <option v-for="prof in professionals" :key="prof.id_prof" :value="prof.id_prof">
                    {{ prof.prof_name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="col-12 mb-3">
                <label class="form-label">Observações</label>
                <textarea
                  v-model="newItem.notes"
                  class="form-control"
                  rows="3"
                  placeholder="Observações adicionais"
                  maxlength="1000">
                </textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddItemModal">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="addItem" :disabled="loading">
              <i v-if="loading" class="fas fa-spinner fa-spin me-2"></i>
              Adicionar Item
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showTaxPdfViewer" class="tax-pdf-viewer-root">
      <div class="tax-pdf-viewer-backdrop" @click="closeTaxPdfViewer"></div>
      <div class="tax-pdf-viewer-panel" role="dialog" aria-modal="true">
        <div class="tax-pdf-viewer-header">
          <div class="fw-semibold text-truncate me-3">{{ pdfFileName || 'Apuração Fiscal' }}</div>
          <div class="d-flex align-items-center gap-2">
            <button type="button" class="btn btn-sm btn-light-primary" @click="downloadTaxPdf" title="Download">
              <i class="bi bi-download"></i>
            </button>
            <button type="button" class="btn btn-sm btn-light-primary" @click="printTaxPdf" title="Imprimir">
              <i class="bi bi-printer"></i>
            </button>
            <button type="button" class="btn btn-sm btn-light-danger" @click="closeTaxPdfViewer" title="Fechar">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
        <iframe
          v-if="taxPdfObjectUrl"
          :key="taxPdfFrameKey"
          :src="taxPdfObjectUrl"
          class="tax-pdf-viewer-frame"
          title="Visualização do relatório de apuração fiscal"
        />
      </div>
    </div>
  </Teleport>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import Modal from "@/helpers/modal";
import Loader from "@/helpers/loader";
import Download from "@/helpers/download";

export default {
  name: "TissBatchDetailModal",
  emits: ['close', 'updated'],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    batch: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      exportLoading: false,
      items: [],
      guidesCount: 0,
      manualItemsCount: 0,
      showAddItemModal: false,
      newItem: {
        description: '',
        amount: '',
        id_prof: '',
        notes: ''
      },
      professionals: [],
      hasTaxSettlements: false,
      availableGuidesForBatch: [],
      selectedGuideIdsForAdd: [],
      loadingAvailableGuides: false,
      addingGuides: false,
      showTaxPdfViewer: false,
      pdfFileName: '',
      taxPdfObjectUrl: null,
      taxPdfFrameKey: 0,
      supplierReportLoadingBillId: null,
      customerReportLoadingBillId: null,
    };
  },
  watch: {
    show(newValue) {
      if (newValue && this.batch) {
        this.loadBatchDetails();
        setTimeout(() => {
          Modal.toggle('#TissBatchDetailModal');
        }, 100);
      }
    },
    'batch.updated_at'() {
      if (this.batchId && this.show) {
        this.loadBatchItems();
      }
    },
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    batchId() {
      return this.batch?.id ?? this.batch?.id_tiss_batch;
    },
    /** Itens para exibição: guias vêm de batch.guides (quando existir) ou de items; manuais só de items */
    displayItems() {
      const guides = this.batch?.guides;
      const rawItems = Array.isArray(this.items) ? this.items : [];
      const manualItems = rawItems.filter(i => i && i.type === 'manual');
      let guideItems;
      if (guides && Array.isArray(guides) && guides.length > 0) {
        guideItems = guides.map((g) => ({
          type: 'guide',
          id: g.id_tiss_guide ?? g.id,
          guide: g,
          patient: g.patient_name ? { patient_name: g.patient_name } : null,
          professional: g.professional_name ? { prof_name: g.professional_name } : null,
          amount: g.amount ?? this.guideAmount(g),
        }));
      } else {
        guideItems = rawItems.filter(i => i && i.type === 'guide');
      }
      return [...guideItems, ...manualItems];
    },
    displayGuidesCount() {
      const guides = this.batch?.guides;
      if (guides && Array.isArray(guides)) return guides.length;
      return this.guidesCount ?? 0;
    },
    hasBillsToReceive() {
      return (this.batch?.bills_to_receive_count ?? this.displayBillsToReceive.length ?? 0) > 0;
    },
    hasBillsToPay() {
      return (this.batch?.bills_to_pay_count ?? this.displayBillsToPay.length ?? 0) > 0;
    },
    displayBillsToReceive() {
      const bills = this.batch?.bills_to_receive;
      if (!bills) return [];
      return Array.isArray(bills) ? bills : Object.values(bills);
    },
    displayBillsToPay() {
      const bills = this.batch?.bills_to_pay;
      if (!bills) return [];
      return Array.isArray(bills) ? bills : Object.values(bills);
    },
  },
  methods: {
    loadBatchDetails() {
      if (!this.batchId || !this.place?.id_place) return;

      // Carregar detalhes do lote
      http.get(`/places/${this.place.id_place}/tiss-batches/${this.batchId}`)
        .then((response) => {
          const batchData = response.data?.data ?? response.data;
          if (this.batch && batchData) {
            Object.assign(this.batch, batchData);
          }
        })
        .catch((error) => {
          console.error('Erro ao carregar detalhes do lote:', error);
        });

      // Carregar itens do lote
      this.loadBatchItems();

      // Carregar profissionais (para seleção em itens avulsos)
      this.loadProfessionals();

      if (this.batch?.batch_type === 'payment') {
        this.loadTaxSettlementsSummary();
      }
    },
    loadBatchItems() {
      if (!this.batchId || !this.place?.id_place) return;

      http.get(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/items`)
        .then((response) => {
          const data = response.data || {};
          let list = data.items ?? (Array.isArray(data) ? data : []);
          if (!Array.isArray(list) && list && typeof list === 'object') {
            list = Object.values(list);
          }
          this.items = Array.isArray(list) ? list : [];
          this.guidesCount = data.guides_count ?? 0;
          this.manualItemsCount = data.manual_items_count ?? 0;
        })
        .catch((error) => {
          console.error('Erro ao carregar itens do lote:', error);
          this.items = [];
        });
    },
    loadProfessionals() {
      this.professionals = this.$store.state.auth.professionals || [];
    },
    loadTaxSettlementsSummary() {
      if (!this.batchId || !this.place?.id_place) {
        return;
      }

      http.get(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/tax-settlements/summary`)
        .then((response) => {
          this.hasTaxSettlements = response.data?.total_settlements > 0
            || (response.data?.total_rpa?.count > 0)
            || (response.data?.total_pj?.count > 0);
        })
        .catch((error) => {
          console.error('Erro ao verificar apurações fiscais:', error);
          this.hasTaxSettlements = false;
        });
    },
    async exportPdf(type) {
      if (!this.batchId || !this.place?.id_place) {
        return;
      }

      const endpoint = type === 'summary' ? 'export-pdf/summary' : 'export-pdf/detailed';
      const typeLabel = type === 'summary' ? 'resumido' : 'detalhado';
      const fileName = `apuracao_fiscal_${type}_lote_${this.batch.batch_number}.pdf`;

      this.exportLoading = true;

      try {
        const response = await http.get(
          `/places/${this.place.id_place}/tiss-batches/${this.batchId}/tax-settlements/${endpoint}`,
          { responseType: 'blob' }
        );

        if (response.data?.type && response.data.type.includes('application/json')) {
          throw Object.assign(new Error('Erro ao gerar relatório PDF'), { response });
        }

        const blob = new Blob([response.data], { type: 'application/pdf' });
        this.openTaxPdfViewer(blob, fileName);
      } catch (error) {
        console.error('Erro ao exportar PDF:', error);
        const errorMessage = await this.handleBlobError(error);
        Alert.toastError(errorMessage);
      } finally {
        this.exportLoading = false;
      }
    },
    billId(bill) {
      return bill?.id ?? bill?.id_bill_to_pay;
    },
    async exportSupplierReport(bill) {
      const id = this.billId(bill);
      if (!this.batchId || !this.place?.id_place || !id) {
        return;
      }

      const supplierName = (bill.supplier?.name || 'fornecedor')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9_-]+/g, '_');
      const fileName = `relatorio_fornecedor_${this.batch.batch_number}_${supplierName}.pdf`;

      this.supplierReportLoadingBillId = id;

      try {
        const response = await http.get(
          `/places/${this.place.id_place}/tiss-batches/${this.batchId}/tax-settlements/export-pdf/supplier`,
          {
            params: { bill_id: id },
            responseType: 'blob',
          }
        );

        if (response.data?.type && response.data.type.includes('application/json')) {
          throw Object.assign(new Error('Erro ao gerar relatório do fornecedor'), { response });
        }

        const blob = new Blob([response.data], { type: 'application/pdf' });
        this.openTaxPdfViewer(blob, fileName);
      } catch (error) {
        console.error('Erro ao exportar relatório do fornecedor:', error);
        const errorMessage = await this.handleBlobError(error);
        Alert.toastError(errorMessage);
      } finally {
        this.supplierReportLoadingBillId = null;
      }
    },
    async exportReceiptPdf(type) {
      if (!this.batchId || !this.place?.id_place) {
        return;
      }

      const endpoint = type === 'summary' ? 'export-pdf/summary' : 'export-pdf/detailed';
      const typeLabel = type === 'summary' ? 'resumido' : 'detalhado';
      const fileName = `relatorio_recebimento_${type}_lote_${this.batch.batch_number}.pdf`;

      this.exportLoading = true;

      try {
        const response = await http.get(
          `/places/${this.place.id_place}/tiss-batches/${this.batchId}/receipt-reports/${endpoint}`,
          { responseType: 'blob' }
        );

        if (response.data?.type && response.data.type.includes('application/json')) {
          throw Object.assign(new Error('Erro ao gerar relatório PDF'), { response });
        }

        const blob = new Blob([response.data], { type: 'application/pdf' });
        this.openTaxPdfViewer(blob, fileName);
      } catch (error) {
        console.error('Erro ao exportar relatório de recebimento:', error);
        const errorMessage = await this.handleBlobError(error);
        Alert.toastError(errorMessage);
      } finally {
        this.exportLoading = false;
      }
    },
    billReceiveId(bill) {
      return bill?.id ?? bill?.id_bill_to_receive;
    },
    async exportCustomerReport(bill) {
      const id = this.billReceiveId(bill);
      if (!this.batchId || !this.place?.id_place || !id) {
        return;
      }

      const customerName = (bill.customer?.name || 'cliente')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9_-]+/g, '_');
      const fileName = `relatorio_cliente_${this.batch.batch_number}_${customerName}.pdf`;

      this.customerReportLoadingBillId = id;

      try {
        const response = await http.get(
          `/places/${this.place.id_place}/tiss-batches/${this.batchId}/receipt-reports/export-pdf/customer`,
          {
            params: { bill_id: id },
            responseType: 'blob',
          }
        );

        if (response.data?.type && response.data.type.includes('application/json')) {
          throw Object.assign(new Error('Erro ao gerar relatório do cliente'), { response });
        }

        const blob = new Blob([response.data], { type: 'application/pdf' });
        this.openTaxPdfViewer(blob, fileName);
      } catch (error) {
        console.error('Erro ao exportar relatório do cliente:', error);
        const errorMessage = await this.handleBlobError(error);
        Alert.toastError(errorMessage);
      } finally {
        this.customerReportLoadingBillId = null;
      }
    },
    openTaxPdfViewer(blob, fileName) {
      if (this.taxPdfObjectUrl) {
        URL.revokeObjectURL(this.taxPdfObjectUrl);
        this.taxPdfObjectUrl = null;
      }

      this.pdfFileName = fileName;
      this.taxPdfObjectUrl = URL.createObjectURL(blob);
      this.taxPdfFrameKey += 1;
      this.showTaxPdfViewer = true;
    },
    closeTaxPdfViewer() {
      this.showTaxPdfViewer = false;
      if (this.taxPdfObjectUrl) {
        URL.revokeObjectURL(this.taxPdfObjectUrl);
        this.taxPdfObjectUrl = null;
      }
      this.pdfFileName = '';
    },
    downloadTaxPdf() {
      if (!this.taxPdfObjectUrl) return;
      const link = document.createElement('a');
      link.href = this.taxPdfObjectUrl;
      link.download = this.pdfFileName || 'apuracao_fiscal.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    printTaxPdf() {
      if (!this.taxPdfObjectUrl) return;
      const win = window.open(this.taxPdfObjectUrl);
      if (win) {
        win.addEventListener('load', () => {
          win.focus();
          win.print();
        });
      }
    },
    async handleBlobError(error) {
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          return json.message || json.error || 'Erro ao gerar relatório PDF';
        } catch {
          return 'Erro ao gerar relatório PDF';
        }
      }

      return error.response?.data?.message || error.message || 'Erro ao gerar relatório PDF';
    },
    closeBatch() {
      Swal.fire({
        title: "Tem certeza?",
        text: "Tem certeza que deseja fechar este lote?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, fechar lote",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33"
      }).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          Loader.show('Fechando lote...');

          http.post(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/close`)
            .then((response) => {
              Loader.close();
              const billsCount = response.data?.bills_count || 0;
              const billKind = this.batch?.batch_type === 'payment' ? 'a pagar' : 'a receber';
              if (billsCount > 0) {
                Alert.toastSuccess(`Lote fechado e ${billsCount} conta(s) ${billKind} gerada(s)!`);
              } else {
                Alert.toastError('Lote fechado, mas nenhuma conta foi gerada.');
              }
              this.$emit('updated', response.data);
              this.loadBatchDetails();
            })
            .catch((error) => {
              Loader.close();
              const data = error.response?.data;
              const msg = typeof data === 'string' && data.trim()
                ? data
                : (data?.error || data?.message || 'Erro ao fechar lote');
              Alert.toastError(msg);
              console.error(error);
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    },
    generateTiss() {
      this.loading = true;
      Loader.show('Gerando XML...');

      http.post(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/generate-tiss`)
        .then((response) => {
          Loader.close();
          const xmls = response.data.xmls;
          
          if (xmls && xmls.length > 0) {
            // Baixar o primeiro XML (ou todos se necessário)
            const firstXml = xmls[0];
            const blob = new Blob([firstXml.xml], { type: 'application/xml' });
            Download.downloadFile(blob, firstXml.filename, 'application/xml');
            
            if (xmls.length > 1) {
              Alert.toastInfo(`XML gerado. ${xmls.length} arquivos foram criados (um por convênio).`);
            } else {
              Alert.toastSuccess('XML gerado e baixado com sucesso!');
            }
          }
          
          this.$emit('updated', response.data);
          this.loadBatchDetails();
        })
        .catch((error) => {
          Loader.close();
          Alert.toastError(error.response?.data?.error || 'Erro ao gerar XML');
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    generateBillsToReceive() {
      Swal.fire({
        title: "Tem certeza?",
        text: "Deseja gerar contas a receber para este lote?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, gerar contas a receber",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33"
      }).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          Loader.show('Gerando contas a receber...');

          http.post(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/generate-bills-to-receive`)
            .then((response) => {
              Loader.close();
              Alert.toastSuccess(`Contas a receber geradas com sucesso! ${response.data.bills.length} conta(s) criada(s).`);
              if (response.data.batch && this.batch) {
                Object.assign(this.batch, response.data.batch);
              }
              this.$emit('updated', response.data);
              this.loadBatchDetails();
            })
            .catch((error) => {
              Loader.close();
              Alert.toastError(error.response?.data?.error || 'Erro ao gerar contas a receber');
              console.error(error);
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    },
    generateBillsToPay() {
      Swal.fire({
        title: "Tem certeza?",
        text: "Deseja gerar contas a pagar para este lote?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, gerar contas a pagar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33"
      }).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          Loader.show('Gerando contas a pagar...');

          http.post(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/generate-bills-to-pay`)
            .then((response) => {
              Loader.close();
              const count = response.data.bills?.length ?? 0;
              Alert.toastSuccess(`Contas a pagar geradas com sucesso! ${count} conta(s) criada(s).`);
              if (response.data.batch && this.batch) {
                Object.assign(this.batch, response.data.batch);
              }
              this.$emit('updated', response.data);
              this.loadBatchDetails();
            })
            .catch((error) => {
              Loader.close();
              Alert.toastError(error.response?.data?.error || 'Erro ao gerar contas a pagar');
              console.error(error);
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    },
    closeModal() {
      this.closeTaxPdfViewer();
      Modal.toggle('#TissBatchDetailModal');
      this.$emit('close');
    },
    statusClass(status) {
      const classes = {
        draft: 'badge badge-secondary',
        closed: 'badge badge-primary',
        sent: 'badge badge-info',
        processed: 'badge badge-success',
        cancelled: 'badge badge-danger',
        generated: 'badge badge-primary',
        rejected: 'badge badge-warning',
      };
      return classes[status] || 'badge badge-secondary';
    },
    statusLabel(status) {
      const labels = {
        draft: 'Rascunho',
        closed: 'Fechado',
        sent: 'Enviado',
        processed: 'Processado',
        cancelled: 'Cancelado',
        generated: 'Gerada',
        rejected: 'Glosada',
      };
      return labels[status] || status;
    },
    batchTypeClass(type) {
      const classes = {
        tiss: 'badge badge-info',
        payment: 'badge badge-warning',
      };
      return classes[type] || 'badge badge-secondary';
    },
    batchTypeLabel(type) {
      const labels = {
        tiss: 'Recebimento',
        payment: 'Pagamento',
      };
      return labels[type] || type;
    },
    billReceiveStatusLabel(status) {
      if (status === 'received') return 'Recebido';
      if (status === 'negotiated') return 'Negociado';
      return 'Pendente';
    },
    billReceiveStatusClass(status) {
      if (status === 'received') return 'badge badge-success';
      if (status === 'negotiated') return 'badge badge-secondary';
      return 'badge badge-warning';
    },
    billPayStatusLabel(status) {
      return status === 'paid' ? 'Pago' : 'Pendente';
    },
    billPayStatusClass(status) {
      return status === 'paid' ? 'badge badge-success' : 'badge badge-warning';
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('pt-BR');
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    openAddItemModal() {
      this.newItem = {
        description: '',
        amount: '',
        id_prof: '',
        notes: ''
      };
      this.$nextTick(() => {
        Modal.toggle('#AddItemModal');
      });
    },
    closeAddItemModal() {
      Modal.toggle('#AddItemModal');
    },
    addItem() {
      if (!this.newItem.description || !this.newItem.amount) {
        Alert.toastError('Preencha pelo menos descrição e valor');
        return;
      }

      this.loading = true;
      http.post(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/items`, {
        description: this.newItem.description,
        amount: parseFloat(this.newItem.amount),
        id_prof: this.newItem.id_prof || null,
        notes: this.newItem.notes
      })
      .then(() => {
        Alert.toastSuccess('Item adicionado com sucesso!');
        this.closeAddItemModal();
        this.loadBatchItems();
        // Recarregar detalhes do lote para atualizar o total
        this.loadBatchDetails();
      })
      .catch((error) => {
        console.error('Erro ao adicionar item:', error);
        Alert.toastError(error.response?.data?.message || 'Erro ao adicionar item');
      })
      .finally(() => {
        this.loading = false;
      });
    },
    removeItem(item) {
      Swal.fire({
        title: "Tem certeza?",
        text: "Esta ação não pode ser desfeita!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, remover!",
        cancelButtonText: "Cancelar"
      }).then(result => {
        if (result.isConfirmed) {
          this.loading = true;
          http.delete(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/items/${item.item.id_tiss_batch_item}`)
            .then(() => {
              Alert.toastSuccess('Item removido com sucesso!');
              this.loadBatchItems();
              this.loadBatchDetails();
            })
            .catch((error) => {
              console.error('Erro ao remover item:', error);
              Alert.toastError('Erro ao remover item');
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    },
    getGuideIdFromItem(item) {
      if (item.type !== 'guide' || !item.guide) return null;
      return item.guide.id_tiss_guide ?? item.guide.id;
    },
    removeGuide(item) {
      const guideId = this.getGuideIdFromItem(item);
      if (!guideId) return;
      Swal.fire({
        title: "Remover guia do lote?",
        text: "A guia será desvinculada do lote e poderá ser adicionada a outro lote depois.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, remover",
        cancelButtonText: "Cancelar"
      }).then(result => {
        if (result.isConfirmed) {
          this.loading = true;
          http.delete(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/guides/${guideId}`)
            .then((response) => {
              const res = response.data || response;
              Alert.toastSuccess(res.message || 'Guia removida do lote com sucesso');
              if (res.batch) Object.assign(this.batch, res.batch);
              if (res.guides_count !== undefined) this.guidesCount = res.guides_count;
              if (res.total_amount !== undefined) this.batch.total_amount = res.total_amount;
              this.loadBatchItems();
              this.$emit('updated', res.batch);
            })
            .catch((error) => {
              const data = error.response?.data;
              Alert.toastError(data?.message || data?.error || 'Erro ao remover guia do lote');
              console.error(error);
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    },
    openAddGuidesModal() {
      this.selectedGuideIdsForAdd = [];
      this.availableGuidesForBatch = [];
      this.$nextTick(() => {
        Modal.toggle('#AddGuidesToBatchModal');
        this.fetchAvailableGuidesForBatch();
      });
    },
    closeAddGuidesModal() {
      Modal.toggle('#AddGuidesToBatchModal');
      this.selectedGuideIdsForAdd = [];
      this.availableGuidesForBatch = [];
    },
    guideIdForAdd(g) {
      return g.id_tiss_guide ?? g.id;
    },
    guideAmount(guide) {
      if (!guide) return 0;
      const g = guide.guide || guide;
      if (g.amount != null && g.amount !== '') {
        return parseFloat(g.amount) || 0;
      }
      if (this.batch?.batch_type === 'payment') {
        return parseFloat(g.procedure_transfer_value ?? 0) || 0;
      }
      return parseFloat(g.procedure_value ?? 0) || 0;
    },
    toggleSelectAllAvailableGuides() {
      if (this.selectedGuideIdsForAdd.length === this.availableGuidesForBatch.length) {
        this.selectedGuideIdsForAdd = [];
      } else {
        this.selectedGuideIdsForAdd = this.availableGuidesForBatch.map(g => this.guideIdForAdd(g));
      }
    },
    fetchAvailableGuidesForBatch() {
      if (!this.batch?.batch_type || !this.place?.id_place) return;
      this.loadingAvailableGuides = true;
      http.get(`/places/${this.place.id_place}/tiss-guides/available-for-batch`, {
        params: { batch_type: this.batch.batch_type }
      })
        .then((response) => {
          const list = response.data?.data ?? response.data ?? [];
          this.availableGuidesForBatch = Array.isArray(list) ? list : [];
        })
        .catch((error) => {
          const data = error.response?.data;
          if (error.response?.status === 422) Alert.toastError(data?.error || 'batch_type inválido');
          else if (error.response?.status === 403) Alert.toastError(data?.error || 'Sem permissão');
          else Alert.toastError('Erro ao carregar guias disponíveis');
          console.error(error);
        })
        .finally(() => {
          this.loadingAvailableGuides = false;
        });
    },
    addGuidesToBatch() {
      if (this.selectedGuideIdsForAdd.length === 0) return;
      this.addingGuides = true;
      http.post(`/places/${this.place.id_place}/tiss-batches/${this.batchId}/guides`, {
        guide_ids: this.selectedGuideIdsForAdd.map(id => Number(id))
      })
        .then((response) => {
          const res = response.data || response;
          Alert.toastSuccess(res.message || `${res.guides_added ?? this.selectedGuideIdsForAdd.length} guia(s) adicionada(s) ao lote`);
          if (res.batch) Object.assign(this.batch, res.batch);
          if (res.guides_count !== undefined) this.guidesCount = res.guides_count;
          if (res.total_amount !== undefined) this.batch.total_amount = res.total_amount;
          this.closeAddGuidesModal();
          this.loadBatchItems();
          this.loadBatchDetails();
          this.$emit('updated', res.batch);
        })
        .catch((error) => {
          const data = error.response?.data;
          if (error.response?.status === 422) {
            Alert.toastError(data?.message || 'Nenhuma guia foi adicionada. Verifique se as guias estão disponíveis para este tipo de lote.');
          } else {
            Alert.toastError(data?.error || data?.message || 'Erro ao adicionar guias ao lote');
          }
          console.error(error);
        })
        .finally(() => {
          this.addingGuides = false;
        });
    },
  },
};
</script>

<style scoped>
.tax-pdf-viewer-root {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.tax-pdf-viewer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
}

.tax-pdf-viewer-panel {
  position: relative;
  z-index: 1;
  width: min(1100px, 100%);
  height: min(90vh, 900px);
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tax-pdf-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eff2f5;
  background: #f9f9f9;
}

.tax-pdf-viewer-frame {
  flex: 1;
  width: 100%;
  border: 0;
  background: #525659;
}
</style>

